import Middleware from '../middleware';

class Auth {
  constructor(ctx) {
    this.ctx = ctx;
    this.store = ctx.store;
    this.interval = null;

    /** To be able to use in <template/> sections */
    this.logout = this.logout.bind(this);
    this.initToken();
  }

  /**
   * Request Authorization header
   * @returns {String} header value
   */
  get requestHeader() {
    return `Bearer ${this.token}`;
  }

  /**
   * Redirect path after logout
   * @returns {String} pathname
   */
  get logoutPath() {
    const { localePath, router } = this.ctx.app;
    /** Check if there is a page named login.vue */
    const loginPageExists = router.options.routes.find(
      ({ name = '' }) => name.split('___')[0] === 'login'
    );

    const logoutPath = loginPageExists ? 'login' : 'index';
    const hasNuxtI18n = !!localePath;
    return hasNuxtI18n ? this.ctx.app.localePath(logoutPath) : `/${logoutPath}`;
  }

  /**
   * Initial mount action, refresh if token is present
   */
  async mount() {
    if (this.token) {
      if (process.server || process.env.SPA) {
        await this.refresh();
        this.store.commit('auth/SET', { key: 'loggedIn', value: true });
      }

      if (process.browser) {
        this.autoRefresh();
      }
    }
  }

  /** Load token from cookies on init */
  initToken() {
    const token = this.ctx.app.$cookies.get('auth.token') || this.ctx.query.token;

    if (token && Auth.parseToken(token).mfa) {
      // Delete existing token if MFA is enabled
      this.setToken('');
    } else {
      // Otherwise just take existing token
      this.setToken(token, false);
    }
  }

  /**
   * Login using credentials in payload
   * Types: [client|admin]
   * @param {Object} payload
   * @param {String} type
   */
  async login(payload, type = 'client') {
    const resp = await this.ctx.$api.dispatch('auth/login', payload, type);
    const { success, data } = resp;

    if (success) {
      this.store.commit('auth/SET', { key: 'user', value: data });
      this.store.commit('auth/SET', { key: 'loggedIn', value: true });
      this.autoRefresh();
    }

    return resp;
  }

  /**
   * Sync user state with ERP
   */
  async fetchUser() {
    const { success, data } = await this.ctx.$api.dispatch('auth/fetch');

    if (success) {
      this.store.commit('auth/SET', { key: 'user', value: data });
    } else {
      this.logout();
    }
  }

  /**
   * Extend JWT token lifespan
   */
  async refresh() {
    const { success, data } = await this.ctx.$api.dispatch('auth/refresh');

    if (success) {
      this.store.commit('auth/SET', { key: 'user', value: data });
    } else {
      this.logout();
    }
  }

  /**
   * Initialize JWT token auto-refresh
   * @param {*} state
   */
  autoRefresh(state = true) {
    if (state) {
      this.interval = setInterval(() => {
        this.refresh();
      }, 1000 * 60 * 14);
    } else {
      clearInterval(this.interval);
    }
  }

  /**
   * Logout and redirect to home page
   */
  logout() {
    if (this.token) this.ctx.$api.dispatch('auth/logout');
    this.setToken('');
    if (this.loggedIn) this.redirectAfterLogout();
    this.store.commit('auth/SET', { key: 'user', value: {} });
    this.store.commit('auth/SET', { key: 'loggedIn', value: false });
  }

  /**
   * Redirect away on logout
   */
  redirectAfterLogout() {
    this.ctx.softRedirect(this.logoutPath);
  }

  /**
   * Extract payload from token middle section
   * @returns {Object} - payload
   */
  static parseToken(token) {
    const splitToken = token.split('.');
    const tokenLength = splitToken.length;
    const base64Url = splitToken[1];

    if (tokenLength !== 3) return {};
    if (process.browser) {
      return JSON.parse(window.atob(base64Url));
    }

    const buff = Buffer.from(base64Url, 'base64').toString('ascii');
    return JSON.parse(buff);
  }

  // Set token into cookies
  setToken(value = '', setCookie = true) {
    const parseToken = Auth.parseToken(value);
    this.token = value;

    if (setCookie) this.ctx.app.$cookies.set('auth.token', value);
    this.store.commit('auth/SET', { key: 'mfa', value: Boolean(parseToken.mfa) });
    this.store.commit('auth/SET', { key: 'type', value: parseToken.account_type });
  }

  /**
   * Get loggedIn status from store
   * @returns {Boolean}
   */
  get loggedIn() {
    return this.store.getters['auth/loggedIn'];
  }

  /**
   * Get user object from store
   * @returns {Object}
   */
  get user() {
    return this.store.state.auth.user;
  }

  /**
   * Get 2fa status from store
   * @returns {Boolean}
   */
  get mfa() {
    return this.store.state.auth.mfa;
  }

  /**
   * Determine if user is client
   * @returns {Boolean}
   */
  get isClient() {
    return this.store.getters['auth/isClient'];
  }

  /**
   * Determine if user is admin
   * @returns {Boolean}
   */
  get isAdmin() {
    return this.store.getters['auth/isAdmin'];
  }

  /**
   * Determine token type
   * @returns {String}
   */
  get type() {
    return this.store.state.auth.type;
  }
}

export default async (ctx, inject) => {
  const backOfficeUrl = '' || 'back-office';

  ctx.app.$api.register('auth', '/web', request => ({
    login: (payload, type = 'client') =>
      type === 'client'
        ? request('POST', '/public/client/login', payload)
        : request('POST', '/public/user/login', payload),
    fetch: () => {
      const { isClient, isAdmin, user } = ctx.app.$auth;
      if (isClient) return request('GET', '/private/client');
      if (isAdmin) return request('GET', `/private/${backOfficeUrl}/user/${user.id}`);
      return { success: false };
    },
    refresh: () => {
      const { type } = ctx.app.$auth;
      if (type === 'client') return request('GET', '/private/client/token/refresh');
      if (type === 'user') return request('GET', `/private/${backOfficeUrl}/token/refresh`);
      return { success: false };
    },
    logout: () => {
      const { token, requestHeader } = ctx.app.$auth;
      const { account_type: type } = Auth.parseToken(token);
      const headers = { Authorization: requestHeader };
      if (type === 'client') return request('POST', '/private/client/logout', {}, { headers });
      if (type === 'user') return request('POST', '/private/user/logout', {}, { headers });
      return { success: true };
    },
  }));

  ctx.store.registerModule(
    'auth',
    {
      namespaced: true,
      state: () => ({
        loggedIn: false,
        type: '',
        user: {},
        mfa: false,
      }),
      getters: {
        /**
         * Check if client is loggedIn
         * @param {Object} state
         * @returns {Booleab}
         */
        loggedIn({ loggedIn, user, mfa }) {
          return Boolean(loggedIn && Object.keys(user).length && !mfa);
        },
        /**
         * Check if token type corresponds to client
         * @param {Object} state
         * @returns {Boolean}
         */
        isClient({ type }, { loggedIn }) {
          return loggedIn && type === 'client';
        },
        /**
         * Check if token type corresponds to user
         * @param {Object} state
         * @returns {Boolean}
         */
        isAdmin({ type }, { loggedIn }) {
          return loggedIn && type === 'user';
        },
      },
      /* eslint-disable no-param-reassign */
      mutations: {
        /**
         * Universal setter for existing state properties
         * @param {Object} state
         * @param {Object} payload
         * @param {String} payload.key - target property key
         * @param {*} payload.value - new value
         */
        SET(state, { key, value }) {
          if (key in state) state[key] = value;
        },
      },
    },
    { preserveState: process.browser && !process.env.SPA }
  );

  const auth = new Auth(ctx);

  // Automatically inject Authorization header
  ctx.$axios.onRequest(req => {
    const isCalculatorRequest = req.url
      .split('/')
      .filter(Boolean)
      .includes('anon');

    if (auth.token && !isCalculatorRequest) req.headers.Authorization = auth.requestHeader;
  });

  // Automatically grab any incoming Authorization headers
  ctx.$axios.onResponse(res => {
    const { Authorization } = res.data;
    if (Authorization) auth.setToken(Authorization);
  });

  // Automatically logout on attempting to access private routes
  ctx.$axios.onError(err => {
    const statusCode = parseInt(err.response && err.response.status, 10);

    if ([401, 403].includes(statusCode)) {
      auth.logout();
    }
  });

  inject('auth', auth);
  ctx.$auth = auth;
  await auth.mount();

  Middleware.auth = async ({ app }) => {
    const { $sandbox } = app;
    if ($sandbox && $sandbox.isCallable()) {
      await $sandbox.start();
      return;
    }

    if (!app.$auth.loggedIn) {
      auth.redirectAfterLogout();
    }
  };
};
